import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingBag, User, Search } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "./Logo";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useLang } from "../context/LanguageContext";

const NAV = [
  { to: "/", tk: "nav.accueil" },
  { to: "/femme", tk: "nav.femme" },
  { to: "/homme", tk: "nav.homme" },
  { to: "/coffrets", tk: "nav.coffrets" },
  { to: "/serum", tk: "nav.serum" },
  { to: "/#resultats", tk: "nav.resultats" },
  { to: "/#ingredients", tk: "nav.ingredients" },
  { to: "/#avis", tk: "nav.avis" },
  { to: "/faq", tk: "nav.faq" },
  { to: "/contact", tk: "nav.contact" },
];

function LangSwitch({ compact }) {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center gap-1" data-testid="lang-switch" style={{ fontSize: "0.6rem" }}>
      {["fr", "en"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-${l}`}
          className="px-1.5 py-0.5 uppercase tracking-[0.15em] transition-colors"
          style={{
            color: lang === l ? "var(--lo-rose-hi)" : "var(--lo-muted)",
            fontWeight: lang === l ? 600 : 400,
            borderBottom: lang === l ? "1px solid var(--lo-rose-hi)" : "1px solid transparent",
          }}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { count } = useCart();
  const { user } = useAuth();
  const { universe, setUniverse } = useTheme();
  const { t } = useLang();
  const loc = useLocation();
  const nav = useNavigate();

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const switchUniverse = (u) => { setUniverse(u); nav(`/${u}`); };
  const isActive = (to) => (to.startsWith("/#") ? false : loc.pathname === to);
  // Les ancres (#resultats/#ingredients/#avis) vivent sur la home d'univers, pas sur la Landing
  const homeBase = universe === "homme" ? "/homme" : "/femme";
  const resolveTo = (to) => (to.startsWith("/#") ? `${homeBase}${to.slice(1)}` : to);

  return (
    <header className="fixed top-[34px] left-0 right-0 z-40 transition-all duration-500 border-b"
      style={{ zIndex: open ? 70 : 40,
               background: scrolled ? "color-mix(in srgb, var(--lo-bg) 88%, transparent)" : "transparent",
               backdropFilter: scrolled ? "blur(18px)" : "none", borderColor: scrolled ? "var(--lo-border)" : "transparent" }}>
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-20 flex items-center justify-between gap-4">
        {/* Mobile: hamburger */}
        <button className="lg:hidden" style={{ color: "var(--lo-text)" }} onClick={() => setOpen(true)} data-testid="menu-open-btn" aria-label="Menu">
          <Menu size={22} />
        </button>

        <div className="absolute left-1/2 -translate-x-1/2 lg:static lg:translate-x-0 flex-shrink-0"><Logo size="sm" /></div>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-6 xl:gap-7 flex-1 justify-center">
          {NAV.map((n) => {
            const femmeHomme = n.to === "/femme" || n.to === "/homme";
            const active = isActive(n.to) || (femmeHomme && universe === n.to.slice(1) && loc.pathname === n.to);
            return (
              <Link key={n.to} to={resolveTo(n.to)} className="link-underline overline whitespace-nowrap"
                style={{ color: active ? "var(--lo-rose-hi)" : "var(--lo-text)", fontSize: "0.62rem" }} data-testid={`nav-${n.tk}`}>
                {t(n.tk)}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-4 md:gap-5">
          <div className="hidden md:block"><LangSwitch /></div>
          <Search size={18} className="hidden md:block cursor-pointer transition-colors" style={{ color: "var(--lo-muted)" }} />
          <button onClick={() => nav(user ? "/compte" : "/connexion")} data-testid="account-btn" aria-label="Compte">
            <User size={18} style={{ color: "var(--lo-text)" }} />
          </button>
          <button className="relative" onClick={() => nav("/panier")} data-testid="cart-btn" aria-label="Panier">
            <ShoppingBag size={19} style={{ color: "var(--lo-text)" }} />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 w-4 h-4 rounded-full text-[10px] flex items-center justify-center" style={{ background: "var(--lo-rose)", color: "var(--lo-btn-text)" }} data-testid="cart-count">{count}</span>
            )}
          </button>
        </div>
      </div>

      {/* Mobile panel */}
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 lg:hidden flex flex-col" style={{ background: "var(--lo-bg)" }} data-testid="mobile-menu">
            <div className="flex items-center justify-between px-5 h-16 border-b" style={{ borderColor: "var(--lo-border)" }}>
              <Logo size="sm" />
              <button onClick={() => setOpen(false)} data-testid="menu-close-btn" aria-label="Fermer" className="w-10 h-10 flex items-center justify-center rounded-full" style={{ border: "1px solid var(--lo-border-rose)", color: "var(--lo-text)" }}><X size={20} /></button>
            </div>

            <div className="px-5 pt-6">
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => switchUniverse("femme")} className="h-14 uppercase tracking-[0.2em] text-xs font-semibold transition-all"
                  style={{ background: universe === "femme" ? "var(--lo-btn-grad)" : "transparent", color: universe === "femme" ? "var(--lo-btn-text)" : "var(--lo-text)", border: "1px solid var(--lo-border-rose)" }} data-testid="mobile-femme-btn">{t("nav.femme")}</button>
                <button onClick={() => switchUniverse("homme")} className="h-14 uppercase tracking-[0.2em] text-xs font-semibold transition-all"
                  style={{ background: universe === "homme" ? "var(--lo-btn-grad)" : "transparent", color: universe === "homme" ? "var(--lo-btn-text)" : "var(--lo-text)", border: "1px solid var(--lo-border-rose)" }} data-testid="mobile-homme-btn">{t("nav.homme")}</button>
              </div>
              <div className="flex justify-center mt-5"><LangSwitch /></div>
            </div>

            <nav className="flex-1 overflow-y-auto px-5 py-6 flex flex-col gap-1">
              {NAV.filter((n) => n.to !== "/femme" && n.to !== "/homme").map((n, i) => (
                <motion.div key={n.to} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                  <Link to={resolveTo(n.to)} className="block py-3.5 border-b font-display text-2xl" style={{ borderColor: "var(--lo-border)", color: "var(--lo-text)" }} data-testid={`mobile-nav-${n.tk}`}>{t(n.tk)}</Link>
                </motion.div>
              ))}
            </nav>

            <div className="px-5 py-5 border-t flex items-center justify-around" style={{ borderColor: "var(--lo-border)" }}>
              <button onClick={() => nav(user ? "/compte" : "/connexion")} className="flex flex-col items-center gap-1" style={{ color: "var(--lo-text)" }} data-testid="mobile-account-btn"><User size={20} /><span className="overline" style={{ fontSize: "0.5rem" }}>{t("nav.compte")}</span></button>
              <button className="flex flex-col items-center gap-1" style={{ color: "var(--lo-text)" }} data-testid="mobile-search-btn"><Search size={20} /><span className="overline" style={{ fontSize: "0.5rem" }}>{t("nav.recherche")}</span></button>
              <button onClick={() => nav("/panier")} className="relative flex flex-col items-center gap-1" style={{ color: "var(--lo-text)" }} data-testid="mobile-cart-btn"><ShoppingBag size={20} />{count > 0 && <span className="absolute -top-1 right-3 w-4 h-4 rounded-full text-[10px] flex items-center justify-center" style={{ background: "var(--lo-rose)", color: "var(--lo-btn-text)" }}>{count}</span>}<span className="overline" style={{ fontSize: "0.5rem" }}>{t("nav.panier")}</span></button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
